import {
  AlertCircle,
  Fingerprint,
  Link2,
  Lock,
  MessageSquareText,
  Search,
  Sparkles,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ThemeToggle } from '@/components/theme-toggle';
import { useSession } from '@/lib/auth';
import { missingAuthConfig } from '@/lib/env';

/** Google's brand mark. lucide-react has no brand icons, so this one is inlined. */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="h-5 w-5" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59A14.4 14.4 0 0 1 9.77 24c0-1.6.28-3.14.76-4.59l-7.98-6.19A23.94 23.94 0 0 0 0 24c0 3.88.93 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

interface Feature {
  Icon: typeof Link2;
  title: string;
  description: string;
}

/** What SLC does, told without a single real link: this page is shown to people who may not see any. */
const FEATURES: readonly Feature[] = [
  {
    Icon: MessageSquareText,
    title: 'Collected as it is shared',
    description:
      'Links posted in the Slack channels SLC is invited to are picked up automatically. Nobody has to bookmark anything.',
  },
  {
    Icon: Fingerprint,
    title: 'One record per link',
    description:
      'Tracking parameters and URL variants are stripped, so each link appears once, credited to whoever shared it first.',
  },
  {
    Icon: Sparkles,
    title: 'Read and summarised',
    description:
      'Every link gets its title, a short summary, categories and a preview image, filled in for you.',
  },
  {
    Icon: Search,
    title: 'Easy to find again',
    description: 'Search the catalogue and filter it by category, channel, domain or date.',
  },
];

function Notice({ tone, children }: { tone: 'destructive' | 'warning'; children: ReactNode }) {
  return (
    <div
      role="alert"
      className={
        tone === 'destructive'
          ? 'flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm'
          : 'flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm'
      }
    >
      <AlertCircle
        aria-hidden="true"
        className={`mt-0.5 h-4 w-4 shrink-0 ${tone === 'destructive' ? 'text-destructive' : 'text-warning'}`}
      />
      <span>{children}</span>
    </div>
  );
}

/**
 * Shown whenever there is no usable session and the catalogue is private, so it must explain SLC
 * without revealing anything from it: no links, no counts, no names. Everything here is static.
 */
export function SignInPage() {
  const session = useSession();
  const missing = missingAuthConfig();
  const misconfigured = missing.length > 0;

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-background">
      <div aria-hidden="true" className="seez-spectrum" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-48 -top-48 -z-10 h-[36rem] w-[36rem] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-56 -right-40 -z-10 h-[32rem] w-[32rem] rounded-full bg-accent blur-3xl"
      />

      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"
          >
            <Link2 className="h-5 w-5" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">SLC</span>
            <span className="text-xs text-muted-foreground">Seez Links Collector</span>
          </span>
        </div>
        <ThemeToggle />
      </header>

      <main className="mx-auto grid w-full max-w-6xl flex-1 content-center gap-10 px-6 pb-16 pt-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-x-16 lg:gap-y-12">
        <section className="flex flex-col gap-5 lg:col-start-1 lg:row-start-1">
          <p className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-1 text-xs font-medium text-muted-foreground">
            <Lock aria-hidden="true" className="h-3.5 w-3.5" />
            Internal to Seez
          </p>
          <h1 className="text-balance text-3xl font-semibold tracking-tight sm:text-5xl">
            Every link shared in Slack, in one catalogue.
          </h1>
          <p className="max-w-xl text-pretty text-lg text-muted-foreground">
            The articles, tools and docs colleagues share in Slack scroll away within days. SLC
            keeps them: one record for each unique link, read, summarised and categorised, so the
            good ones are easy to find again.
          </p>
        </section>

        <Card className="w-full self-center shadow-lg lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <CardHeader className="gap-2 p-6 pb-4">
            <h2 className="text-xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground">
              Use your Seez Google account. The catalogue can include links from private channels,
              so it is only shown to signed-in colleagues.
            </p>
          </CardHeader>

          <CardContent className="flex flex-col gap-4 p-6 pt-0">
            {session.status === 'error' && session.error ? (
              <Notice tone="destructive">
                Sign-in failed: {session.error.message}. Please try again, or contact the platform
                team if it keeps happening.
              </Notice>
            ) : null}

            {misconfigured ? (
              <Notice tone="warning">
                Authentication is not configured. Missing environment{' '}
                {missing.length === 1 ? 'variable' : 'variables'}:{' '}
                <code className="font-mono text-xs">{missing.join(', ')}</code>.
              </Notice>
            ) : null}

            <Button
              type="button"
              size="lg"
              variant="outline"
              className="w-full"
              disabled={misconfigured || session.status === 'loading'}
              loading={session.status === 'loading'}
              onClick={session.signIn}
            >
              {session.status === 'loading' ? null : <GoogleMark />}
              Continue with Google
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              You will be redirected to the Seez identity provider. Access is limited to Seez
              accounts.
            </p>
          </CardContent>
        </Card>

        <section aria-labelledby="what-slc-does" className="lg:col-start-1 lg:row-start-2">
          <h2 id="what-slc-does" className="sr-only">
            What SLC does
          </h2>
          <ul className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            {FEATURES.map(({ Icon, title, description }) => (
              <li key={title} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground"
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">{title}</span>
                  <span className="text-sm text-muted-foreground">{description}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className="mx-auto w-full max-w-6xl px-6 pb-6 text-xs text-muted-foreground">
        SLC keeps one record per unique link, owned by the first person who shared it.
      </footer>
    </div>
  );
}
